// @flow

import { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import type { Miliseconds, Toast } from '../../typings';
import DisplayText from '../Text/DisplayText';
import { TOAST_DISPLAY_TIME, ANIMATION_DURATION, SMALL_DEVICE_WIDTH } from '../../../constants';
import { PIN_PAD_WIDTH } from '../PinPad/constants';

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${props => (props.type && props.theme.toast[props.type]) || 'success'};
  border: 1px solid ${props => (props.type && props.theme.toast[props.type]) || 'success'};
  border-radius: 8px;
  border: none;
  min-height: 75px;
  width: ${props => (props.deviceWidth <= SMALL_DEVICE_WIDTH ? PIN_PAD_WIDTH : 312)}px;
  padding-left: 30px;
  padding-right: 20px;
  padding-top: ${props => (props.verticalPadding ? '10px' : '0')};
  padding-bottom: ${props => (props.verticalPadding ? '10px' : '0')};
  margin: 10px 0;
`;

const UnstyledButton = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  border-radius: 8px;
  background-color: transparent;
  opacity: ${props => (props.visible ? 1 : 0)};
  transition: all 0.7s ease;
  transform: ${props => (props.visible ? 'translateY(0)' : 'translateY(100px)')};
  z-index: 8;

  &:focus {
    outline: 0;
  }
`;

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 8px;
`;

type Props = {
  toast: Toast,
  closeCallback: () => void,
  deviceWidth: number
};

type State = {
  visible: boolean,
  text: string,
  timer: string
};

export default class CustomToast extends PureComponent<Props, State> {
  componentDidMount() {
    const displayTime = this.props.toast.displayTime || TOAST_DISPLAY_TIME;
    this.showTimeout = setTimeout(() => {
      this.setState({ visible: true });
    }, ANIMATION_DURATION);

    this.autocloseTimeout = setTimeout(() => {
      this.close();
    }, ANIMATION_DURATION + displayTime);

    this.setState({ text: this.props.toast.text });
    if (this.props.toast.includeTimer) {
      this.setTimer(displayTime);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.showTimeout);
    clearTimeout(this.autocloseTimeout);
    clearTimeout(this.closeTimeout);
    clearInterval(this.textUpdateInterval);
  }

  state = {
    visible: false,
    text: '',
    timer: ''
  };

  showTimeout: TimeoutID;
  autocloseTimeout: TimeoutID;
  closeTimeout: TimeoutID;
  textUpdateInterval: IntervalID;

  setTimer(displayTime: Miliseconds) {
    let durationLeft = displayTime;
    let durationLeftInSeconds = Math.floor(displayTime / 1000);
    this.setState({ timer: `${durationLeftInSeconds}s ...` });
    this.textUpdateInterval = setInterval(() => {
      durationLeft = durationLeft - 1000;
      durationLeftInSeconds = Math.floor(durationLeft / 1000);
      this.setState({ timer: `${durationLeftInSeconds}s ...` });
    }, 1000);
  }

  close() {
    this.setState({ visible: false });
    this.closeTimeout = setTimeout(() => {
      this.props.closeCallback();
    }, ANIMATION_DURATION);
  }

  renderText(text: Array<string> | string) {
    if (Array.isArray(text)) {
      return (text.map: any)(this.multilineTextMapper);
    }

    return (
      <DisplayText fontSize="16px" bold>
        {text}
      </DisplayText>
    );
  }

  multilineTextMapper = (textLine: string, index: number, arrayOfLines: Array<string>) => {
    const lastIndex = arrayOfLines.length - 1;
    const line = index === lastIndex ? `${textLine} ${this.state.timer}` : textLine;
    return (
      <Spacer key={index}>
        <DisplayText fontSize="16px" bold>
          {line}
        </DisplayText>
      </Spacer>
    );
  };

  render() {
    return (
      <UnstyledButton onClick={() => this.close()} visible={this.state.visible}>
        <ToastContainer
          style={{ opacity: 1 }}
          deviceWidth={this.props.deviceWidth}
          type={this.props.toast.type}
          verticalPadding={typeof this.state.text !== 'string'}>
          {this.renderText(this.state.text)}
        </ToastContainer>
      </UnstyledButton>
    );
  }
}

CustomToast.propTypes = {
  toast: PropTypes.object,
  deviceWidth: PropTypes.number
};
